import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Lock, Clock, User, Home as HomeIcon, Briefcase, CalendarDays, ArrowRight, MapPin, Info } from 'lucide-react';
import { IMAGES, HOME_TRUST_BADGES, LIFESTYLE_FEATURES, WHY_CHOOSE_CARDS } from '../data/mock';
import Testimonials from '../components/Testimonials';

const ICONS = { ShieldCheck, Lock, Clock, User, Home: HomeIcon, Briefcase, CalendarDays };

const Home = () => {
  return (
    <div className="bg-white">
      {/* Hero */}
      <section className="relative bg-[#0f1113] text-white overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${IMAGES.hero})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0f1113] via-[#0f1113]/85 to-transparent" />
        <div className="relative max-w-[1280px] mx-auto px-6 py-24 md:py-32 lg:py-40">
          <div className="max-w-xl">
            <p className="text-[11px] tracking-[0.3em] text-[#c9a961] mb-6 font-medium">
              MOBILE PHLEBOTOMY &amp; WELLNESS SERVICES
            </p>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight mb-6" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
              Exceptional Care.<br />Wherever You Are.
            </h1>
            <p className="text-white/80 text-base leading-relaxed mb-10 max-w-md">
              Aurelia Concierge Wellness Partners brings premium, discreet, and professional phlebotomy and wellness services directly to your home, office, or preferred location.
            </p>
            <Link
              to="/contact"
              className="inline-block bg-[#c9a961] hover:bg-[#b89751] transition-colors duration-300 text-[#141618] tracking-[0.22em] text-[12px] font-semibold px-8 py-4"
            >
              BOOK YOUR APPOINTMENT
            </Link>

            {/* Trust badges */}
            <div className="grid grid-cols-4 gap-5 mt-14 max-w-lg">
              {HOME_TRUST_BADGES.map((b) => {
                const Icon = ICONS[b.icon];
                return (
                  <div key={b.label} className="flex flex-col items-center text-center">
                    <div className="w-11 h-11 rounded-full border border-[#c9a961]/70 flex items-center justify-center mb-3">
                      <Icon size={18} className="text-[#c9a961]" strokeWidth={1.4} />
                    </div>
                    <p className="text-[9px] tracking-[0.22em] text-white/85 leading-[1.6] whitespace-pre-line font-medium">
                      {b.label}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Lifestyle features band */}
        <div className="relative max-w-[1280px] mx-auto px-6 pb-16">
          <div className="bg-[#141618] border border-white/10 py-10 px-6">
            <p className="text-center text-[11px] tracking-[0.3em] text-[#c9a961] font-medium mb-8">
              CONCIERGE CARE THAT FITS YOUR LIFESTYLE
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 divide-y md:divide-y-0 md:divide-x divide-white/10">
              {LIFESTYLE_FEATURES.map((f) => {
                const Icon = ICONS[f.icon];
                return (
                  <div key={f.title} className="px-4 flex flex-col items-center text-center">
                    <Icon size={26} className="text-[#c9a961] mb-4" strokeWidth={1.3} />
                    <h3 className="text-[12px] tracking-[0.25em] font-semibold mb-3">{f.title}</h3>
                    <p className="text-white/70 text-[13px] leading-relaxed">{f.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Why choose Aurelia */}
      <section className="bg-[#f5f0ea] py-20 md:py-24">
        <div className="max-w-[1280px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <p className="text-[11px] tracking-[0.3em] text-[#c9a961] font-medium mb-5">
              WHY CHOOSE AURELIA
            </p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.1] text-[#1c1e20] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
              Elevated Care.<br />Uncompromising Standards.
            </h2>
            <div className="w-16 h-[2px] bg-[#c9a961] mb-6" />
            <p className="text-[#4a4a4a] leading-relaxed text-[15px] max-w-sm mb-8">
              We combine clinical expertise with white-glove service to deliver a seamless experience from start to finish.
            </p>
            <Link
              to="/about"
              className="inline-block border border-[#1c1e20] hover:bg-[#1c1e20] hover:text-white transition-colors duration-300 text-[#1c1e20] tracking-[0.22em] text-[11px] font-semibold px-6 py-3"
            >
              LEARN MORE ABOUT US
            </Link>
          </div>

          <div className="lg:col-span-8 grid grid-cols-2 md:grid-cols-4 gap-6">
            {WHY_CHOOSE_CARDS.map((c) => (
              <div key={c.title} className="flex flex-col">
                <div className="aspect-square bg-white overflow-hidden mb-4">
                  <img src={c.image} alt={c.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                </div>
                <h3 className="text-[11px] tracking-[0.24em] font-semibold text-[#1c1e20] whitespace-pre-line mb-2">
                  {c.title}
                </h3>
                <p className="text-[#4a4a4a] text-[13px] leading-relaxed">{c.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Area & Policies */}
      <section className="bg-white py-16 md:py-20 border-t border-black/5">
        <div className="max-w-[1100px] mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-[11px] tracking-[0.3em] text-[#c9a961] font-medium mb-4">SERVICE AREA & POLICIES</p>
            <h2
              className="font-serif text-4xl text-[#1c1e20] mb-3"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
            >
              A few things to know before booking.
            </h2>
            <div className="w-16 h-[2px] bg-[#c9a961] mx-auto" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-[#faf6f0] border border-black/5 p-8">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-11 h-11 rounded-full border border-[#c9a961] flex items-center justify-center flex-shrink-0">
                  <MapPin size={18} className="text-[#c9a961]" strokeWidth={1.5} />
                </div>
                <h3
                  className="font-serif text-2xl text-[#1c1e20]"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
                >
                  Based in Decatur, GA
                </h3>
              </div>
              <p className="text-[#4a4a4a] leading-relaxed text-[15px]">
                A mileage fee is added for any location over 20 miles from Decatur, GA. The fee is discussed during the initial phone call and is based on total round-trip mileage.
              </p>
            </div>
            <div className="bg-[#faf6f0] border border-black/5 p-8">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-11 h-11 rounded-full border border-[#c9a961] flex items-center justify-center flex-shrink-0">
                  <Info size={18} className="text-[#c9a961]" strokeWidth={1.5} />
                </div>
                <h3
                  className="font-serif text-2xl text-[#1c1e20]"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
                >
                  Cancellation Policy
                </h3>
              </div>
              <p className="text-[#4a4a4a] leading-relaxed text-[15px]">
                Cancellations incur a $40 flat fee. Same-day cancellations are not refunded.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <Testimonials />

      {/* CTA band */}
      <section className="relative bg-[#141618] text-white overflow-hidden">
        <div
          className="absolute inset-0 opacity-30 bg-cover bg-center"
          style={{ backgroundImage: `url(${IMAGES.luxuryInterior})` }}
        />
        <div className="absolute inset-0 bg-[#141618]/85" />
        <div className="relative max-w-[1280px] mx-auto px-6 py-16 grid grid-cols-1 md:grid-cols-12 items-center gap-8">
          <div className="md:col-span-1 flex md:justify-center">
            <img src="/brand/aurelia-logo-v2.png" alt="Aurelia" className="w-32 md:w-36 h-auto object-contain" />
          </div>
          <div className="md:col-span-6">
            <h3 className="font-serif text-3xl md:text-4xl leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
              Your Time Is Valuable.<br />Your Health Is Priceless.
            </h3>
          </div>
          <div className="md:col-span-5">
            <p className="text-white/80 mb-5 max-w-md">
              Let Aurelia Concierge Wellness Partners bring premium care to you.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-3 bg-[#c9a961] hover:bg-[#b89751] transition-colors duration-300 text-[#141618] tracking-[0.22em] text-[12px] font-semibold px-7 py-3.5"
            >
              BOOK NOW <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
